import React, { useEffect, useMemo } from 'react';
import {
    CallRequestDetailContainerProps,
    CallRequestDetailViewProps,
} from './callRequestDetailProps';
import { ChatContainerBaseConfig } from '../../../base';
import {
    CallRequestDetailController,
    CallRequestDetailLoader,
} from './callRequestDetailInterface';
import { createCallPreset } from '@varos/call-preset';
import { createAssistantSdk } from '@varos/assistant-sdk';
import { EnvironmentVariables } from '../../../../../../../config/environment';
import { assert } from '@varos/util-typescript';
import { createChatStrategy } from '@varos/assistant-react-chat';

export function createCallRequestDetailContainer(
    config: ChatContainerBaseConfig,
    loader: CallRequestDetailLoader,
    controller: CallRequestDetailController
): React.FC<CallRequestDetailContainerProps> {
    const {
        context: { useContext },
        kernel: {
            infra: {
                toaster: { useToast },
            },
        },
        hook,
    } = config;

    const {
        call: { useCall },
    } = createCallPreset({
        debugIceServers: false,
        client: createAssistantSdk({
            baseUrl: EnvironmentVariables.ASSISTANT_API_BASE_URL,
        }),
        autoStart: true,
    });

    // Configure chat strategy with auto-reply enabled
    const { useChat } = createChatStrategy({
        autoReply: {
            enabled: true,
            // Use default condition: last message is from user AND no assistant messages exist
        },
    });

    return config.state.composer.subscribe(({ as: View, ...containerProps }) => {
        const context = useContext();
        const toast = useToast();
        const state = {
            composer: config.state.composer.useState(context),
        };

        const auth = hook.useAuth();
        assert(auth.scheme.kind === 'legacy', 'legacy auth scheme is required');
        assert(auth.scheme.store.authToken, 'auth token is required');

        const chat = useChat(context, {
            options: {
                mode: 'solution',
                environmentMode: state.composer.value.test ? 'test' : 'live',
                tag: state.composer.value.version,
                workspace: context.workspace.id,
            },
            token: auth.scheme.store.authToken,
            messages: useMemo(
                () => containerProps.item.thread.messages.map((msg) => msg.message),
                [containerProps.item.thread.messages]
            ),
            threadState: 'preparing',
            threadId: containerProps.item.thread.thread.id,
            onGenerated() {
                console.log('onGenerated');
            },
            async onMessageCreate(message) {
                console.log('onMessageCreate', message);
            },
            async onMessageUpdate(message) {
                throw new Error('Not implemented');
            },
            async onCreateDocument(args) {
                throw new Error('Not implemented');
            },
            async onReplaceDocument(args) {
                throw new Error('Not implemented');
            },
        });

        const call = useCall(context, containerProps.item, {
            options: {
                // TODO get these from API
                username: 'test',
                password: 'test',
                uris: ['turn:turn.varos.ai:3478'],
                ttl: 1000,
            },
            async onConnect() {
                console.log('Call connected from call overlay');
            },
            async onEnd(call) {
                console.log('call ended from request container', call);
            },
            async onError(error: Error) {
                console.error(error);
                toast({
                    kind: 'error',
                    description: 'Call connection failed',
                });
            },
            onUserSpeaking() {
                console.log('User is speaking');
            },
            onSystemSpeaking() {
                console.log('System is speaking');
            },
        });

        const props = controller.useProps(context, containerProps.item, {
            async onEndRequested() {
                const result = await call.end();
                // await chat.reply({
                //     context: [],
                //     parts: [],
                // });
                return containerProps.onEnd(result);
            },
        });
        return <View {...props} />;
    });
}
